import { ChangeEvent, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  Autocomplete,
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import MDButton from "components/MDButton";

import {
  PATROCINADORES_ACCENT,
  patrocinadoresFieldSx,
} from "./patrocinadores-evento.theme";

import { IPatrocinador } from "interfaces/patrocinador";
import { vincularPatrocinadorEvento } from "services/patrocinadores";
import { setMessage, useMaterialUIController } from "context";

const PATROCINADORES_CADASTRO_PATH = "/dashboards/patrocinadores";

interface Props {
  eventoId: number;
  options: IPatrocinador[];
  handleClose: () => void;
  updateEvento: () => void;
  onRefreshOptions: () => void;
  refreshingOptions?: boolean;
}

export default function FormVincularPatrocinador({
  eventoId,
  options,
  handleClose,
  updateEvento,
  onRefreshOptions,
  refreshingOptions = false,
}: Props) {
  const [, dispatch] = useMaterialUIController();
  const [selectedSponsor, setSelectedSponsor] = useState<IPatrocinador | null>(
    null,
  );
  const [ordem, setOrdem] = useState("0");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedSponsor) return;

    setSubmitting(true);
    try {
      await vincularPatrocinadorEvento(
        selectedSponsor.id,
        eventoId,
        Number(ordem) || 0,
      );
      setMessage(dispatch, {
        open: true,
        text: "Patrocinador vinculado ao evento",
        severity: "success",
      });
      handleClose();
      updateEvento();
    } catch {
      setMessage(dispatch, {
        open: true,
        text: "Erro ao vincular patrocinador",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", boxSizing: "border-box" }}>
      <Stack spacing={2}>
        <Stack spacing={1.25}>
          <Autocomplete
            fullWidth
            options={options}
            getOptionLabel={(option) => option.nome}
            value={selectedSponsor}
            onChange={(_, value) => setSelectedSponsor(value)}
            noOptionsText="Nenhum patrocinador disponível"
            loading={refreshingOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Patrocinador"
                required
                InputLabelProps={{ shrink: true }}
                sx={patrocinadoresFieldSx}
              />
            )}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <MDButton
              component={RouterLink}
              to={PATROCINADORES_CADASTRO_PATH}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              color="dark"
              size="small"
              startIcon={<OpenInNewOutlinedIcon />}
              sx={{ flexShrink: 0 }}
            >
              Cadastrar novo patrocinador
            </MDButton>
            <MDButton
              variant="text"
              color="dark"
              size="small"
              onClick={onRefreshOptions}
              disabled={refreshingOptions}
              startIcon={
                refreshingOptions ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshOutlinedIcon />
                )
              }
              sx={{ flexShrink: 0 }}
            >
              Atualizar lista
            </MDButton>
          </Stack>

          {options.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Cadastre um patrocinador ou clique em &quot;Atualizar lista&quot;
              após o cadastro para vinculá-lo aqui.
            </Typography>
          ) : null}
        </Stack>

        <TextField
          type="number"
          label="Ordem de exibição"
          name="ordem"
          variant="outlined"
          fullWidth
          value={ordem}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setOrdem(e.target.value)
          }
          InputLabelProps={{ shrink: true }}
          sx={{ ...patrocinadoresFieldSx, maxWidth: { sm: 240 } }}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <MDButton
            variant="text"
            color="secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancelar
          </MDButton>
          <MDButton
            variant="gradient"
            color="dark"
            disabled={!selectedSponsor || submitting}
            onClick={() => void handleSubmit()}
            sx={{
              minWidth: 140,
              background: `linear-gradient(195deg, ${PATROCINADORES_ACCENT}, #e04a1a)`,
            }}
          >
            {submitting ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Vincular"
            )}
          </MDButton>
        </Stack>
      </Stack>
    </Box>
  );
}
